import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, mergeMap, shareReplay, take, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationService } from './AuthenticationService';
import { PropertyDataApiService } from './PropertyDataApiService';
import { PropertyDTO } from '../DTO/propertyDTO';
import { PropertyImagePoolDTO } from '../DTO/propertyImagePoolDTO';
import { PropertyTrashResponse, RestoreFromTrashResponse, TrashItemKind } from '../models/propertyTrash';
import { CopyPropertyImagesResponse, PhotoUploadLinkResponse, PhotoUploadLinksResponse, ReviewVendorPhotoUploadsRequest, StorePhotoUploadLinkRequest, VendorPhotoUploadsResponse, ProofChange, PropertyProofFollowUpsResponse, PropertyAlbumImagesResponse, PropertyImageAlbumsResponse, PropertyImageTagsResponse, PropertyImageTagSuggestionsResponse, PropertyPhotoReportRequest, PropertyPhotoReportResponse, PropertyProofSummaryResponse, PropertyProofVisitsResponse, ProofLink } from '../models/propertyPhotos';
import { HttpEvent } from '@angular/common/http';
import { Property } from '../models/property';
import { Result } from '../models/result';
import { PropertyType } from '../models/propertyType';
import { SaleType } from '../models/saleType';
import { ApplicationUser } from '../models/applicationUser';
import { BuyerRepresentationRequest } from '../requests/buyerRepresentationRequest';
import { PropertyOffer } from '../models/propertyOffer';
import { PropertyOrder } from '../models/propertyOrder';
import { PropertyAbstract } from '../models/propertyAbstract';
import { AuctionAbstractDTO } from '../DTO/auctionAbstractDTO';
import { AuctionAbstract } from '../models/auctionAbstract';
import { LegalAdRequest } from '../requests/legalAdRequest';
import { LegalAd } from '../models/legalAd';
import { LegalAdDTO } from '../DTO/legalAdDTO';
import { LegalAdVoidRequest } from '../requests/legalAdVoidRequest';
import { SaleTypeDTO } from '../DTO/saleTypeDTO';
import { CreateLegalAdRequest } from '../requests/createLegalAdRequest';
import { AuctionStatusHistory } from '../models/auctionStatusHistory';
import { PropertyViewsPublishingData } from '../models/propertyViewsPublishingData';
import { AuctionAdminDefaults } from '../models/auctionAdminDefaults';
import { Inventory } from '../models/inventory';
import { StoreInventoryResponse } from '../responses/storeInventoryResponse';
import { BasicResponse } from '../responses/basicResponse';
import { Address } from '../models/address';
import { StoreInventoryBulkResponse } from '../responses/storeInventoryBulkResponse';
import { VaultDocumentsResponse } from '../responses/vaultDocumentsResponse';
import { VaultDocument } from '../models/vaultDocument';
import { StoreVaultDocumentResponse } from '../responses/storeVaultDocumentResponse';
import { PropertyAttachmentViewRequest } from '../requests/propertyAttachmentViewRequest';
import { ExecuteLegalDocumentRequest } from '../requests/executeLegalDocumentRequest';
import { DraftLegalDocumentRequest } from '../requests/draftLegalDocumentRequest';
import { FileResponse } from '../responses/fileResponse';
import { VaultDocumentsWithFileResponse } from '../responses/vaultDocumentsWithFileResponse';
import { base64ToFile } from '../helpers/blob.utils';
import { InventoryImageDTO } from '../DTO/inventoryImageDTO';
import { InventoryImage } from '../models/inventoryImage';
import { StoreInventoryImagesResponse } from '../responses/storeInventoryImagesResponse';
import { UserAuctionAuthorizationStatusRequest } from '../requests/userAuctionAuthorizationStatusRequest';
import { UserAuctionAuthorizationStatusResponse } from '../responses/userAuctionAuthorizationStatusResponse';
import { PropertyNote } from '../models/propertyNote';
import { PropertyNotesResponse } from '../responses/propertyNotesResponse';
import { PropertyExpensesResponse } from '../responses/propertyExpensesResponse';
import { PropertyExpense } from '../models/propertyExpense';
import { HotEditRequest } from '../requests/hotEditRequest';
import { AuctionReservePriceResponse } from '../responses/auctionReservePriceResponse';
import { ReserveCreditRequest } from '../requests/reserveCreditRequest';
import { PropertyAbstractDTO } from '../DTO/propertyAbstractDTO';
import { LoggingService } from './LoggingService';
import { FolderStructure } from '../models/folderStructure';
import { FolderStructuresResponse } from '../responses/folderStructuresResponse';
import { StoreFolderStructureResponse } from '../responses/storeFolderStructureResponse';
import { PropertyNoteDTO } from '../DTO/propertyNoteDTO';
import { StorePropertyNoteResponse } from '../responses/storePropertyNoteResponse';
import { PropertyParty } from '../models/propertyParty';
import { PropertyPartyDTO } from '../DTO/propertyPartyDTO';
import { PropertyMasterSettingsDTO } from '../DTO/propertyMasterSettingsDTO';
import { PropertyMasterSettings } from '../models/propertyMasterSettings';
import { PropertyMasterCatalogItemDTO } from '../DTO/propertyMasterCatalogItemDTO';
import { PropertyTaskDTO } from '../DTO/propertyTaskDTO';
import { PropertyTask } from '../models/propertyTask';
import { PropertyPreservationDTO } from '../DTO/propertyPreservationDTO';
import { PropertyPreservation } from '../models/propertyPreservation';
import { PropertyManagementDTO } from '../DTO/propertyManagementDTO';
import { PropertyManagement } from '../models/propertyManagement';
import { SoldProperty, SoldPropertiesPage } from '../models/soldProperty';
import { PropertyFilterParameters } from '../models/propertyFilterParameters';
import { PropertyPhase } from '../models/propertyPhase';
import { InventoryDTO } from '../DTO/inventoryDTO';
import { PropertyOfferDTO } from '../DTO/propertyOfferDTO';
import { PropertyOrderDTO } from '../DTO/propertyOrderDTO';
import { AuctionStatusHistoryDTO } from '../DTO/auctionStatusHistoryDTO';
import { SoldPropertyDTO } from '../DTO/soldPropertyDTO';


@Injectable({ providedIn: 'root' })
export class PropertyService {
    private latestPropertUpdatedTimestamp?:Date;

    private propertyTypeObservableCache: Observable<PropertyType[]> | undefined;
    private propertyTypeCache: PropertyType[] | undefined;
  
    private saleTypeObservableCache: Observable<SaleType[]> | undefined;
    private saleTypeCache: SaleType[] | undefined;
   
    private propertyObservableCache: Observable<Property[]> | undefined;
    private propertyCache: Map<number,Property>  | undefined;


    private propertyAbstractObservableCache: Observable<PropertyAbstract[]>  | undefined;
    private propertyAbstractCache: Map<number,PropertyAbstract>  | undefined;

    private inventoryObservableCache: Observable<Inventory[]>  | undefined;
    private inventoryCache: Map<number,Inventory>  | undefined;

    private propertyNotesObservableCache: {[propertyId:number]: Observable<PropertyNote[]> | undefined} = {};
    private propertyNotesCache: {[propertyId:number]: PropertyNote[]|undefined} = {};

    private propertyIdObservableCache: {[id:number]: Observable<Property|undefined>} = {};
    
    private favoritePropertyIdsObservableCache: Observable<number[]>  | undefined;
    private favoritePropertyIdsCache: number[] | undefined;

    private comparePropertyIdsObservableCache: Observable<number[]> | undefined;
    private comparePropertyIdsCache: number[] | undefined;

    public propertyCacheUpdatedSubject:Subject<void> = new Subject();
    public propertyUpdateSubject:Subject<Property> = new Subject<Property>();

     public propertyPhases:PropertyPhase[] = [
        new PropertyPhase(10, 'Pipeline'),
        new PropertyPhase(20, 'Engaged'),
        new PropertyPhase(30, 'Wind Down'),
        new PropertyPhase(99, 'Terminated')
      ];

    constructor(private pdApiService:PropertyDataApiService, private authService:AuthenticationService, private snackBar: MatSnackBar, private logger:LoggingService){
        this.authService.currentUserSubject.subscribe(au=>this.processCurrentUser(au));
    }
  
    get authorized(){
        return this.authService.authorizedSubject.value;
    }

    get currentUser(){
        return this.authService.currentUserValue;
    }

    private processCurrentUser(au:ApplicationUser|undefined){
        if(!au){
            //user logged out, clear all personal caches
            this.clearPersonalCache();
        }
    }
    
    private clearPersonalCache(){
        this.propertyTypeObservableCache=undefined;
        this.propertyTypeCache=undefined;
        this.saleTypeObservableCache=undefined;
        this.saleTypeCache=undefined;
        this.propertyObservableCache=undefined;
        this.propertyCache=undefined;
        this.propertyAbstractObservableCache=undefined;
        this.propertyAbstractCache=undefined;
        this.propertyIdObservableCache = {};
        this.favoritePropertyIdsObservableCache=undefined;
        this.favoritePropertyIdsCache=undefined;
        this.comparePropertyIdsObservableCache=undefined;
        this.comparePropertyIdsCache=undefined;
        this.inventoryCache=undefined;
        this.inventoryObservableCache=undefined;
        //notes depend on the user (client users only get released notes): never hand them to the next user
        this.propertyNotesObservableCache={};
        this.propertyNotesCache={};
    }

    public updateLatestPropertyUpdatedTimeStamp(p:Property){
        if(!this.latestPropertUpdatedTimestamp || this.latestPropertUpdatedTimestamp<p.updated!) this.latestPropertUpdatedTimestamp=p.updated;
    }

    //#region property type handling
    public getPropertyTypes(){
        // Data available
        if (this.propertyTypeCache) return of(this.propertyTypeCache);
        // Request pending
        else if (this.propertyTypeObservableCache) return this.propertyTypeObservableCache;
        // New request needed
        else this.propertyTypeObservableCache = this.fetchPropertyTypes();

        return this.propertyTypeObservableCache;
    }

    public getPropertyTypeById(id:number){
        return this.getPropertyTypes().pipe(map(ca=>ca.find(c=>c.id==id)));
    }

    private fetchPropertyTypes(){
        return this.pdApiService.getPropertyTypes().pipe(map((data:PropertyType[])=>this.processPropertyTypes(data)), shareReplay(1));
    }

    private processPropertyTypes(data:PropertyType[]){
        this.propertyTypeObservableCache=undefined;
        this.propertyTypeCache=data.map(dto => PropertyType.mapFromDTO(dto));
        return this.propertyTypeCache;
    }
    //#endregion

    //#region legal status handling
    public getSaleTypes(){
        // Data available
        if (this.saleTypeCache) return of(this.saleTypeCache);
        // Request pending
        else if (this.saleTypeObservableCache) return this.saleTypeObservableCache;
        // New request needed
        else this.saleTypeObservableCache = this.fetchSaleTypes();

        return this.saleTypeObservableCache;
    }

    public getSaleTypeById(id:number){
        return this.getSaleTypes().pipe(map(lsa=>lsa.find(s=>s.id==id)));
    }

    private fetchSaleTypes(){
        return this.pdApiService.getSaleTypes().pipe(map((data:SaleTypeDTO[])=>this.processSaleTypes(data)), shareReplay(1));
    }

    private processSaleTypes(data:SaleTypeDTO[]){
        this.saleTypeObservableCache=undefined;
        this.saleTypeCache=data.map(dto => SaleType.mapFromDTO(dto));
        return this.saleTypeCache;
    }

    public updateSaleTypeChecklistItems(st:SaleType){
        return this.pdApiService.updateSaleTypeChecklistItems(st).pipe(map((result:Result)=>this.processUpdateSaleTypeChecklistItems(result, st)));
    }
    private processUpdateSaleTypeChecklistItems(result:Result, st:SaleType){
        if(result.success){
            //update cache
            let existingSaleType = this.saleTypeCache!.find(saleType => saleType.id==st.id)!;
            existingSaleType.checklistItems=st.checklistItems;
            this.snackBar.open('Checklist for "' + st.name + '" has been updated.', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000});
        }
        else{
            this.snackBar.open('Checklist update for "' + st.name + '" failed.', '×', {panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        }
        return result;
    }

    //#endregion

    //#region property handling
    public getProperties(){
        if(!this.authorized) return of([]);
        // If cache exists → return it immediately
        if (this.propertyCache) return of([...this.propertyCache.values()]);
        // If request already in progress → reuse it
        if (this.propertyObservableCache) {
            return this.propertyObservableCache;
        }
        // Otherwise → load from API and fill cache
        this.propertyObservableCache = this.pdApiService.getProperties().pipe(
            map(data => {
                const result: Property[] = [];
                this.propertyCache = new Map<number, Property>();
                data.forEach((dto:PropertyDTO)=> {
                    const p = Property.mapFromDTO(dto, this);
                    this.propertyCache!.set(p.id, p);
                    result.push(p);
                });

                return result;
            }),
            shareReplay(1)
        );

        return this.propertyObservableCache;
    }

    /** one page of the sold properties, filtered and sorted (most recent first) by the server; undefined if loading failed */
    public getSoldPropertiesPage(params:PropertyFilterParameters|undefined, page:number, pageSize:number, includeMarkers:boolean):Observable<SoldPropertiesPage|undefined>{
        if(!this.authorized) return of({total:0, items:[]});
        return this.pdApiService.getSoldPropertiesPage(params, page, pageSize, includeMarkers).pipe(
            map(data => {
                if(!Array.isArray(data?.items)) return undefined;
                return {
                    total: data.total,
                    items: data.items.map((dto:SoldPropertyDTO) => SoldProperty.mapFromDTO(dto, this)),
                    markers: data.markers?.map((dto:SoldPropertyDTO) => SoldProperty.mapFromDTO(dto, this))
                };
            })
        );
    }

    public getPropertyById(id:number, forceUpdate:boolean){
        if(!this.authorized) return of(undefined);
        if(this.propertyCache?.has(id) && !forceUpdate) return of(this.propertyCache.get(id));
        if (this.propertyIdObservableCache[id]) return this.propertyIdObservableCache[id];
        return this.propertyIdObservableCache[id]=this.fetchPropertyById(id);
    }

    private fetchPropertyById(id:number){
        return this.pdApiService.getPropertyById(id).pipe(map((data:PropertyDTO)=>this.processProperty(data, id)), shareReplay(1));
    }

    private processProperty(data:PropertyDTO, id:number){
        delete this.propertyIdObservableCache[id];
        if(data!=null){
            var property=Property.mapFromDTO(data, this);
            if(this.latestPropertUpdatedTimestamp == undefined || this.latestPropertUpdatedTimestamp<property.updated!) this.latestPropertUpdatedTimestamp=property.updated;
            if(this.propertyCache) this.propertyCache.set(property.id, property); //don't create cache if not existing since we need to make sure it gets only created upon full load.
            if(this.propertyAbstractCache){
                let pa = this.propertyAbstractCache.get(property.id);
                if(pa) pa.updateFromProperty(property);
            } 
            this.propertyUpdateSubject.next(property);
            return property;
        }
        return undefined;
    }

    public saveProperty(property:Property, formData:FormData, propertyParties:PropertyParty[], propertyTasks:PropertyTask[], propertyPreservation:PropertyPreservation[], propertyManagement:PropertyManagement[]){
        return this.pdApiService.saveProperty(property, formData, propertyParties, propertyTasks, propertyPreservation, propertyManagement);
    }

    public updateProperty(property:Property, formData:FormData, docFilesToRemove:number[], propertyParties:PropertyParty[], propertyTasks:PropertyTask[], propertyPreservation:PropertyPreservation[], propertyManagement:PropertyManagement[], officialUpdate:boolean){
        return this.pdApiService.updateProperty(property, formData, docFilesToRemove, propertyParties, propertyTasks, propertyPreservation, propertyManagement, officialUpdate);
    }

    public removeProperty(id:number, physicalDelete:boolean){
        return this.pdApiService.deleteProperty(id, physicalDelete).pipe(map((r:Result)=>{
            if(r.success) this.processRemoveProperty(id, physicalDelete);
            return r;
        }));
    }

    public processRemoveProperty(id:number, physicalDelete:boolean){
        this.propertyCache!.delete(id);
        if(this.propertyAbstractCache && physicalDelete){
            this.propertyAbstractCache.delete(id);
        }
    }

    public restoreProperty(id:number){
        return this.pdApiService.restoreProperty(id);
    }
   
    public updatePropertyData(){
        //update after reconnect: use latestPropertUpdatedTimeStamp to ask server to send property data updated after
        return this.pdApiService.getPropertyUpdates(this.latestPropertUpdatedTimestamp).pipe(map((data:PropertyDTO[])=>this.processPropertyUpdates(data)), shareReplay(1));
    }

    private processPropertyUpdates(data:PropertyDTO[]){
        //process data and update cached properties
        data.forEach(dto=>{
            var p = Property.mapFromDTO(dto, this);
            const isSold = (p.auction?.auctionStatus===6 || p.sold===true) && p.showSold===true
            if(this.propertyCache?.has(p.id)){
                //updated property
                this.propertyCache.get(p.id)!.updateFromProperty(p);
                //sold properties are not cached: the sold page loads them from the server
                if(isSold) this.propertyCache.delete(p.id);
            }
            else if(!isSold){
                //new property
                this.propertyCache?.set(p.id,p);
            }
            if(!this.latestPropertUpdatedTimestamp ||this.latestPropertUpdatedTimestamp<p.updated!) this.latestPropertUpdatedTimestamp=p.updated;
        });
        this.propertyCacheUpdatedSubject.next();
    }

    public updatePropertyPhase(propertyId:number, propertyPhase:PropertyPhase){
        return this.pdApiService.updatePropertyPhase(propertyId, propertyPhase.id).pipe(map((r:Result)=>{
            if(r.success){
                this.setPropertyPhase(propertyId,propertyPhase);
            }
            return r;
        }));
    }

    public setPropertyPhase(propertyId:number, propertyPhase:PropertyPhase|number){
        var phase;
        if(propertyPhase instanceof PropertyPhase){
            phase = propertyPhase
        }
        else{
            phase = this.propertyPhases.find(pp=>pp.id==propertyPhase);
        }
        const pa = this.propertyAbstractCache?.get(propertyId);
        if(pa) pa.propertyPhase=phase!;
        const p = this.propertyCache!.get(propertyId);
        if(p) p.propertyPhase=phase!;
    }

    public lockProperty(propertyId:number, clientSessionId:string, epochTimeStamp:number){
        return this.pdApiService.lockProperty(propertyId, clientSessionId, epochTimeStamp);
    }

    public unlockProperty(propertyId:number, clientSessionId:string){
        return this.pdApiService.unlockProperty(propertyId, clientSessionId);
    }

    public forceUnlockProperty(propertyId:number, clientSessionId:string){
        return this.pdApiService.forceUnlockProperty(propertyId, clientSessionId);
    }

    public processPropertyViews(data: PropertyViewsPublishingData){
        const property = this.propertyCache?.get(data.propertyId);
        if(property){
            property.views=data.views;
        }
        if(this.propertyAbstractCache){
            const propertyAbstract = this.propertyAbstractCache.get(data.propertyId);
            if(propertyAbstract){
                propertyAbstract.views=data.views;
            }
        }
    }

    public propertyIsListed(id:number){
        if(!this.propertyCache){
            //nothing loaded yet, load all properties
            return this.getProperties().pipe(map(pa=>pa.findIndex(p=>p.id==id)>-1));
        }
        else{
            //we have a cache, lets check if have the requested property
            return of(this.propertyCache.has(id));
        }
    }

    public getPropertyAbstracts(force:boolean):Observable<PropertyAbstract[]>{
        if(!this.authorized) return of([]); //no need to be authorized to see properties
        // If cache exists → return it immediately
        if (this.propertyAbstractCache) return of([...this.propertyAbstractCache.values()]);
        // If request already in progress → reuse it
        if (this.propertyAbstractObservableCache) {
            return this.propertyAbstractObservableCache;
        }
        // Otherwise → load from API and fill cache
        this.propertyAbstractObservableCache = this.pdApiService.getPropertyAbstracts().pipe(
            map(data => {
                const result: PropertyAbstract[] = [];
                this.propertyAbstractCache = new Map<number, PropertyAbstract>();
                data.forEach((dto: PropertyAbstractDTO) => {
                    const p = PropertyAbstract.mapFromDTO(dto, this);
                    this.propertyAbstractCache!.set(p.id, p);
                    result.push(p);
                });

                return result;
            }),
            shareReplay(1)
        );

        return this.propertyAbstractObservableCache;
    }

    public getPropertyAbstractsForClient(clientId:number){
        return this.getPropertyAbstracts(false).pipe(map((paArray=>paArray.filter(pa=>pa.clientId===clientId))));
    }

    public getPropertyAbstractsForClientContact(userId:string){
        return of(new Array<PropertyAbstract>());  //TODO: need to be able relate properties with a client contact first
    }

    public getPropertyAbstractsForSalesManager(userId:string){
        return this.getPropertyAbstracts(false).pipe(map((paArray=>paArray.filter(pa=>pa.salesManagerId===userId))));
    }

    public getPropertyAbstractsForPropertyManager(userId:string){
        return this.getPropertyAbstracts(false).pipe(map((paArray=>paArray.filter(pa=>pa.propertyManagerId===userId))));
    }

    public getPropertyAbstractsForJudge(judgeId:number){
        return this.getPropertyAbstracts(false).pipe(map((paArray=>paArray.filter(pa=>pa.judgeId===judgeId))));
    }

    public getPropertyAbstractsForPortfolio(portfolioId:number){
        return this.getPropertyAbstracts(false).pipe(map((paArray=>paArray.filter(pa=>pa.portfolioId===portfolioId))));
    }

    public getPropertyAbstractById(id:number){
        const pa = this.propertyAbstractCache?.get(id);
        if (pa) return of(pa);
        return this.pdApiService.getPropertyAbstracts(id).pipe(map((result)=>{
           return this.processPropertyAbstractById(result);
        }), shareReplay(1));
    }

    private processPropertyAbstractById(data:PropertyAbstractDTO[]){
        if(data && data.length>0){
            //only expecting one but don't cache if there is no cache yet
            let propertyAbstract = PropertyAbstract.mapFromDTO(data[0], this);
            if(!this.propertyAbstractCache) return propertyAbstract;
            this.propertyAbstractCache.set(propertyAbstract.id,propertyAbstract);
            return propertyAbstract;
        }
        else return undefined
    }

    public getPropertyForAdmin(id:number){
        return this.pdApiService.getPropertyForAdmin(id).pipe(map((data:PropertyDTO)=>Property.mapFromDTO(data,this)), shareReplay(1));
    }

    public getPropertyOffers(){
        return this.pdApiService.getPropertyOffers().pipe(map(result=>{
            let poArray:PropertyOffer[]=new Array();
            result.forEach((dto:PropertyOfferDTO)=>{
                poArray.push(PropertyOffer.mapFromDTO(dto));
            });
            return poArray;
        }), shareReplay(1));
    }

    public getPropertyOrders(){
        return this.pdApiService.getPropertyOrders().pipe(map(result=>{
            let poArray:PropertyOrder[]=new Array();
            result.forEach((dto:PropertyOrderDTO)=>{
                poArray.push(PropertyOrder.mapFromDTO(dto));
            });
            return poArray;
        }), shareReplay(1));
    }

    public getAuctionStatusHistory(auctionId:number){
        return this.pdApiService.getAuctionStatusHistory(auctionId).pipe(map(result=>{
            let ashArray:AuctionStatusHistory[]=new Array();
            result.forEach((dto:AuctionStatusHistoryDTO)=>{
                ashArray.push(AuctionStatusHistory.mapFromDTO(dto));
            });
            return ashArray;
        }), shareReplay(1));
    }

    public getPropertyScoreboardData(propertyId:number, scheduleSeqNo:number|undefined){
        return this.pdApiService.getPropertyScoreboardData(propertyId,scheduleSeqNo).pipe(map((result:Result)=>{
            return result;
        }), shareReplay(1));
    }

    public getAuctionReservePrice(auctionId:number, scheduleSeqNo?:number){
        return this.pdApiService.getAuctionReservePrice(auctionId,scheduleSeqNo).pipe(map((result:AuctionReservePriceResponse)=>{
            return result;
        }));
    }

    public registerPropertyView(propertyId:number){
        return this.pdApiService.registerPropertyView(propertyId,this.authService.currentUserValue?.id);
    }

     public getPropertyViews(propertyId:number){
        return this.pdApiService.getPropertyViews(propertyId);
    }

    public registerPropertyAttachmentView(pav:PropertyAttachmentViewRequest){
        return this.pdApiService.registerPropertyAttachmentView(pav);
    }

    public hotEditReservePrice(auctionId:number,reservePrice:number){
        let request=new HotEditRequest();
        request.auctionId=auctionId;
        request.reservePrice=reservePrice;
        request.action="ReservePrice";
        request.userId=this.authService.currentUserValue!.id;
        request.businessId=this.authService.currentUserValue!.businessId;
        return this.pdApiService.hotEdit(request);
    }

    public hotEditPostponeEndDate(auctionId:number,postponeEndDate:Date,reason:string,showPostponed:boolean,voidExistingBids:boolean){
        let request=new HotEditRequest();
        request.auctionId=auctionId;
        request.postponeEndDate=postponeEndDate.toISOString();
        request.reason=reason;
        request.action="PostponeEndDate";
        request.userId=this.authService.currentUserValue!.id;
        request.businessId=this.authService.currentUserValue!.businessId;
        request.additionalInstructions = JSON.stringify({ showPostponed, voidExistingBids });
        return this.pdApiService.hotEdit(request);
    }

    public reserveCredit(auctionId:number, scheduleSeqNo:number, reserveCredit:number){
        let request=new ReserveCreditRequest();
        request.auctionId=auctionId;
        request.scheduleSeqNo=scheduleSeqNo;
        request.reserveCredit=reserveCredit;
        request.userId=this.authService.currentUserValue!.id;
        request.businessId=this.authService.currentUserValue!.businessId;
        return this.pdApiService.reserveCredit(request);
    }
    //#endregion

    //#region favorites 
    public getFavoriteProperties(){
        return this.getProperties().pipe(mergeMap(pArray => {
            return this.filterFavorites(pArray).pipe(map(result=>result))
        }));
    }

    private filterFavorites(pArray:Property[]){
        return this.getFavoritePropertyIds().pipe(map((response:BasicResponse|number[])=>{
            if(response instanceof BasicResponse) return undefined
            else return pArray?pArray.filter(p=>(response as number[]).includes(p.id)):undefined;
        }));
    }

    private  getFavoritePropertyIds(){
        if(!this.authorized) return of([]);
        // Data available
        if (this.favoritePropertyIdsCache) return of(this.favoritePropertyIdsCache);
        // Request pending
        else if (this.favoritePropertyIdsObservableCache) return this.favoritePropertyIdsObservableCache;
        //  If request already in progress → reuse it
        if (this.favoritePropertyIdsObservableCache) {
            return this.favoritePropertyIdsObservableCache;
        }
        // Otherwise → load from API and fill cache
        this.favoritePropertyIdsObservableCache = this.pdApiService.getFavoriteProperties().pipe(
            map(data => {
                this.favoritePropertyIdsObservableCache=undefined;
                this.favoritePropertyIdsCache=data;
                return data;
            }),
            shareReplay(1)
        );

        return this.favoritePropertyIdsObservableCache;
    }

    public addToFavorites(p:Property): Observable<Result | undefined> {
        //double check is property is already favorite. Shouldn't be possible due to UI setup, but just in case...
        let index = this.favoritePropertyIdsCache!.indexOf(p.id);
        if(index==-1){
            return this.pdApiService.saveFavoriteProperty(p.id).pipe(take(1),map((r:Result)=>{
                if(r.success){
                    if(this.favoritePropertyIdsCache){
                        this.favoritePropertyIdsCache.push(p.id);
                    }
                    else{
                        this.getFavoritePropertyIds().pipe(take(1)).subscribe(idArray=>{
                            //nothing to do here as we load latest from server including the newly added id
                        });
                    }
                    this.snackBar.open('The property "' + p.header + '" has been added to favorites.', '×', {
                        verticalPosition: 'top',
                        duration: 2000,
                        direction: 'ltr'
                    });
                }
                return r;
            }));
        }
        else return of(undefined);
    }

    public removeFromFavorites(p:Property){
        return this.pdApiService.deleteFavoriteProperty(p.id).pipe(map((r:Result)=>{
            if(r.success){
                let index = this.favoritePropertyIdsCache!.indexOf(p.id);
                if(index>-1) this.favoritePropertyIdsCache!.splice(index,1);
                this.snackBar.open('The property "' + p.header + '" has been removed from your favorites.', '×', {
                verticalPosition: 'top',
                duration: 2000,
                direction: 'ltr'
                });
            }
            return r;
        }));
    }

    public removeAllFavorites(){
        return this.pdApiService.deleteFavoriteProperty(-1).pipe(map((r:Result)=>{
            if(r.success){
                this.favoritePropertyIdsCache!.splice(0,this.favoritePropertyIdsCache!.length);
                this.snackBar.open('All properties have been removed from your favorites.', '×', {
                verticalPosition: 'top',
                duration: 2000,
                direction: 'ltr'
                });
            }
            return r;
        }));
    }

    public isFavorite(propertyId:number):boolean{
        if(this.favoritePropertyIdsCache){
        return this.favoritePropertyIdsCache.includes(propertyId);
        }
        else return false;
    }
    //#endregion

    //#region compare
    public getCompareProperties(){
        return this.getProperties().pipe(mergeMap(pArray => 
            this.filterComparables(pArray).pipe(map(result=>result))));
    }

    private filterComparables(pArray:Property[]){
        return this.getComparePropertyIds().pipe(map((idArray:number[])=>{
            return pArray?pArray.filter(p=>idArray.includes(p.id)):undefined;
        }));
    }

    private  getComparePropertyIds(){
        if(!this.authorized) return of([]);
        // Data available
        if (this.comparePropertyIdsCache) return of(this.comparePropertyIdsCache);
        // Request pending
        else if (this.comparePropertyIdsObservableCache) return this.comparePropertyIdsObservableCache;
        //  If request already in progress → reuse it
        if (this.comparePropertyIdsObservableCache) {
            return this.comparePropertyIdsObservableCache;
        }
        // Otherwise → load from API and fill cache
        this.comparePropertyIdsObservableCache = this.pdApiService.getCompareProperties().pipe(
            map(data => {
                this.comparePropertyIdsObservableCache=undefined;
                this.comparePropertyIdsCache=data;
                return data;
            }),
            shareReplay(1)
        );

        return this.comparePropertyIdsObservableCache;
    }

    public addToComparables(p:Property): Observable<Result | undefined> {
        //double check is property is already comparable. Shouldn't be possible due to UI setup, but just in case...
        let index = this.comparePropertyIdsCache!.indexOf(p.id);
        if(index==-1){
            return this.pdApiService.saveCompareProperty(p.id).pipe(map((r:Result)=>{
                if(r.success){
                    if(this.comparePropertyIdsCache){
                        this.comparePropertyIdsCache.push(p.id);
                    }
                    else{
                        this.getComparePropertyIds().pipe(take(1)).subscribe(idArray=>{
                        //nothing to do here as we load latest from server including the newly added id
                    });
                }
                this.snackBar.open('The property "' + p.header + '" has been added to compare list.', '×', {
                    verticalPosition: 'top',
                    duration: 2000,
                    direction: 'ltr'
                    });
                }
                return r;
            }));
        }
        else return of(undefined)
    }

    public removeFromComparables(p:Property){
        return this.pdApiService.deleteCompareProperty(p.id).pipe(map((r:Result)=>{
            if(r.success){
                let index = this.comparePropertyIdsCache!.indexOf(p.id);
                if(index>-1) this.comparePropertyIdsCache!.splice(index,1);
                this.snackBar.open('The property "' + p.header + '" has been removed from your compare list.', '×', {
                    verticalPosition: 'top',
                    duration: 2000,
                    direction: 'ltr'
                });
            }
            return r;
        }));
    }

    public removeAllComparables(){
        return this.pdApiService.deleteCompareProperty(-1).pipe(map((r:Result)=>{
            if(r.success){
                this.comparePropertyIdsCache!.splice(0,this.comparePropertyIdsCache!.length);
                this.snackBar.open('All properties have been removed from your compare list.', '×', {
                verticalPosition: 'top',
                duration: 2000,
                direction: 'ltr'
                });
            }
            return r;
        }));
    }

    public isComparable(propertyId:number):boolean{
        if(this.comparePropertyIdsCache){
        return this.comparePropertyIdsCache.includes(propertyId);
        }
        else return false;
    }
    //#endregion

    //#region neraby properties
    public getNearbyProperties(p:Property): Observable<Property[]>{
        //get closest properties around geo-coordinates, sort by distance and take up to 6. 
        //currently distance shall be less than 25 miles - might do that configurable later.
        if(p.latitude==undefined || p.longitude==undefined) return of([]);
        return this.getProperties().pipe(mergeMap((paArray:Property[])=>{
            return this.pdApiService.getNearbyProperties(p.latitude!,p.longitude!,160000).pipe(map((data:number[])=>this.processNearbyProperties(p, data)), shareReplay(1)); //160000 is meters, so we are looking in a radius of 100miles
        }));
    }
    private processNearbyProperties(referenceProperty:Property, idArray:number[]){
        return [...this.propertyCache!.values()].filter(p=>idArray.includes(p.id) && p.id!=referenceProperty.id && (p.auction?.auctionIsLive || p.auction?.showComingSoon)); //exclude calling property and properties which are already ended
    }
    //#endregion

    //#region duplicate address check
     public checkDuplicateAddress(streetAddress1:string|undefined, streetAddress2: string|undefined, city: string|undefined, state: string|undefined, zipCode: string|undefined, latitude: number|undefined, longitude: number|undefined, radiusMeters: number|undefined, excludePropertyId: number|undefined){
        return this.pdApiService.checkDuplicateAddress(streetAddress1, streetAddress2, city, state, zipCode, latitude, longitude, radiusMeters, excludePropertyId);
     }
    //#endregion

    //#region geocoding stuff
    public getAddress(lat:number, lng:number){
        var latlng = new google.maps.LatLng(lat, lng);
        var geocoder = new google.maps.Geocoder();
        let request =  { location: latlng };
        return geocoder.geocode(request, (results, status) => { }).then(r=>r.results);
    }

    public getLocation(address:string){
        var geocoder = new google.maps.Geocoder();
        let request =  { address: address, fulfillOnZeroResults:true };
        return geocoder.geocode(request, (results, status) => { }).then(r=>r.results);
    }

    public getStructuredAddress(address:string){
        var geocoder = new google.maps.Geocoder();
        let request =  { address: address};
        return geocoder.geocode(request, (results, status) => results).then(r=> this.extractAddressComponent(r.results));
    }

    public extractAddressComponent(results: google.maps.GeocoderResult[]): Address | undefined {
        let locality: google.maps.GeocoderAddressComponent | undefined;
        let postal_code: google.maps.GeocoderAddressComponent | undefined;
        let administrative_area_level_1: google.maps.GeocoderAddressComponent | undefined;
        let administrative_area_level_2: google.maps.GeocoderAddressComponent | undefined;
        let street_number: google.maps.GeocoderAddressComponent | undefined;
        let route: google.maps.GeocoderAddressComponent | undefined;

        const result = results.find(r =>
            r.address_components.findIndex(ac => ac.types.includes('country') && ac.short_name == 'US') > -1
        );

        if (!result) {
            return undefined;
        }

        result.address_components.forEach(item => {
            if (item.types.indexOf('locality') > -1) {
            locality = item;
            }
            if (item.types.indexOf('postal_code') > -1) {
            postal_code = item;
            }
            if (item.types.indexOf('administrative_area_level_1') > -1) {
            administrative_area_level_1 = item;
            }
            if (item.types.indexOf('administrative_area_level_2') > -1) {
            administrative_area_level_2 = item;
            }
            if (item.types.indexOf('street_number') > -1) {
            street_number = item;
            }
            if (item.types.indexOf('route') > -1) {
            route = item;
            }
        });

        const address = new Address();
        address.country = 'US';
        if (locality) address.city = locality.long_name;
        if (postal_code) address.zipCode = postal_code.long_name;
        if (administrative_area_level_1) address.state = administrative_area_level_1.short_name;
        if (administrative_area_level_2) address.county = administrative_area_level_2.long_name;
        if (route) address.streetAddress1 = (street_number ? street_number.long_name + ' ' : '') + route.short_name;

        return address;
    }
    //#endregion

    //#region defaults handling
    public getAuctionAdminDefaults(){
        return this.pdApiService.getAuctionAdminDefaults().pipe(map((r:Result)=>{
            return Object.assign(new AuctionAdminDefaults, JSON.parse(r.message!)) as AuctionAdminDefaults;
        }), shareReplay(1));
    }
    //#endregion

    //#region buyer representation
    public representBuyer(brr:BuyerRepresentationRequest){
        return this.pdApiService.representBuyer(brr).pipe(map(r=>this.processRepresentBuyerResult(r)));
    }
    private processRepresentBuyerResult(r:Result){
        if(r.success){
            //update representations
            this.authService.getUserBrokerBuyerRepresentations();
        }
        return r;
    }

    public confirmRepresentation(token:string){
        return this.pdApiService.confirmRepresentation(token);
    }

    public brokerRepresentation(propertyId:number){
        return this.authService.getUserBuyerBrokerRepresentations(propertyId);
    }

    public brokerRepresentationsForProperty(propertyId:number){
        return this.authService.getUserBuyerBrokerRepresentationsForProperty(propertyId);
    }
    //#endregion

    //#region legal ad
    public createLegalAd(request:CreateLegalAdRequest){
        return this.pdApiService.createLegalAd(request);
    }
    public saveLegalAd(request:LegalAdRequest){
        return this.pdApiService.saveLegalAd(request);
    }
    public updateLegalAd(request:LegalAdRequest){
        return this.pdApiService.updateLegalAd(request);
    }
    public voidLegalAd(request:LegalAdVoidRequest){
        return this.pdApiService.voidLegalAd(request);
    }

    public getAuctionAbstracts(auctionType:number){
        return this.pdApiService.getAuctionAbstracts(auctionType).pipe(map(data=>this.processAuctionAbstractsResult(data)), shareReplay(1));
    }
    public getAuctionAbstract(auctionId:number){
        return this.pdApiService.getAuctionAbstract(auctionId).pipe(map(data=>this.processAuctionAbstractsResult(data)), shareReplay(1));
    }
    private processAuctionAbstractsResult(data:AuctionAbstractDTO[]):AuctionAbstract[]{
        let aaArray = new Array<AuctionAbstract>();
        data?.forEach(dto=>{
            let aa = AuctionAbstract.mapFromDTO(dto);
            aaArray.push(aa);
        });
        return aaArray;
    }

    public getLegalAds(includeVoid:boolean){
        return this.pdApiService.getLegalAds(includeVoid).pipe(map(data=>this.processLegalAdsResult(data)), shareReplay(1));
    }
    private processLegalAdsResult(data:LegalAdDTO[]){
        let laArray = new Array<LegalAd>();
        data.forEach(dto=>{
            let la = LegalAd.mapFromDTO(dto);
            laArray.push(la);
        });
        return laArray;
    }

    public getLegalAd(legalAdId:number){
        return this.pdApiService.getLegalAd(legalAdId).pipe(map(data=>this.processLegalAdResult(data)));
    }
    public getLegalAdForAuction(auctionId:number){
        return this.pdApiService.getLegalAdForAuction(auctionId).pipe(map(data=>this.processLegalAdResult(data)));
    }
    private processLegalAdResult(data:LegalAdDTO){
        let la = LegalAd.mapFromDTO(data);
        return la;
    }

    
    //#endregion

    //#region inventory handling
    public getInventory(){
        if(!this.authorized) return of([]);
        // If cache exists → return it immediately
        if (this.inventoryCache) return of([...this.inventoryCache.values()]);
        // If request already in progress → reuse it
        if (this.inventoryObservableCache) {
            return this.inventoryObservableCache;
        }
        // Otherwise → load from API and fill cache
        this.inventoryObservableCache = this.pdApiService.getInventory().pipe(
            map(response => {
                const result: Inventory[] = [];
                this.inventoryCache = new Map<number, Inventory>();
                response.inventory.forEach((dto:InventoryDTO) => {
                    const i = Inventory.mapFromDTO(dto);
                    this.inventoryCache!.set(i.id!, i);
                    result.push(i);
                });

                return result;
            }),
            shareReplay(1)
        );

        return this.inventoryObservableCache;
    }

    public storeInventory(i:Inventory){
        return this.pdApiService.storeInventory(i).pipe(map((r:StoreInventoryResponse)=>{
            if(r.success){
                if(this.inventoryCache){
                    const updated_inventory=Inventory.mapFromDTO(r.inventory!);
                    this.inventoryCache.set(updated_inventory.id!, updated_inventory);
                }
            }
            else{
                this.logger.log(r.message);
            }
            return r;
        }));
    }

    public storeInventoryBulk(iArray:Inventory[]){
        return this.pdApiService.storeInventoryBulk(iArray).pipe(map((r:StoreInventoryBulkResponse)=>{
            if(r.success){
                if(this.inventoryCache){
                    r.inventory.forEach(i => {
                        const updated_inventory=Inventory.mapFromDTO(i);
                        this.inventoryCache!.set(updated_inventory.id!, updated_inventory);
                    });
                }
            }
            else{
                this.logger.log(r.message);
            }
            return r;
        }));
    }

    public deleteInventory(i:Inventory){
        return this.pdApiService.deleteInventory(i).pipe(map((r:BasicResponse)=>{
            if(r.success){
                if(this.inventoryCache){
                    this.inventoryCache.delete(i.id!)
                }
            }
            else{
                this.logger.log(r.message);
            }
            return r;
        }));
    }

    public storeInventoryImages(i:Inventory, images:InventoryImage[]){
        return this.pdApiService.storeInventoryImages(i, images).pipe(map((response:StoreInventoryImagesResponse)=>this.processInventoryImagesResponse(i, response.images)));
    }

    public getInventoryImages(i:Inventory|number){
        return this.pdApiService.getInventoryImages(i).pipe(map((response:InventoryImageDTO[])=>this.processInventoryImagesResponse(i, response)), shareReplay(1));
    }

    private processInventoryImagesResponse(i:Inventory|number, response:InventoryImageDTO[]){
        if(response){
            let iiArray = response.map(dto => InventoryImage.mapFromDTO(dto));
            if(i instanceof Inventory) i.images=iiArray;
            return iiArray;
        }
        else return [];
    }
    //#endregion

    //#region folder structures
    public getFolderStructures(clientId:number|undefined, context:string|undefined, contextId:number|undefined){
        //only load if user logged in
        if(this.authService.currentUserValue) return this.pdApiService.getFolderStructures(this.authService.currentUserValue.id, clientId, context, contextId).pipe(map(r=> {return this.processGetFolderStructuresResponse(r)}));
        else return of([] as FolderStructure[]);
    }

    private processGetFolderStructuresResponse(r:FolderStructuresResponse){
        let fsArray:FolderStructure[]=[];
        if(r.success){
            r.folderStructures.forEach(dto=>fsArray.push(FolderStructure.mapFromDTO(dto)));
        }
        return fsArray;
    }

    public storeFolderStructure(fs:FolderStructure){
        if(!fs.id) {
            fs.businessId=this.authService.currentUserValue!.businessId;
            fs.addedByUser=this.authService.currentUserValue!.id;
        }
        fs.lastEditedByUser=this.authService.currentUserValue!.id;
        return this.pdApiService.storeFolderStructure(fs, this.authService.currentUserValue!.id).pipe(map(r=>this.processStoreFolderStructureResponse(r)));
    }

    private processStoreFolderStructureResponse(r:StoreFolderStructureResponse){
        if(r.success){
            //let dto=r.vaultDocument;
            //let j=VaultDocument.mapFromDTO(dto);
            //not sure if we really need to process the dto here
            //once cache exist => update in cache
        }
        return r;
    }
    //#endregion

    //#region vault documents
    public getVaultDocuments(clientId:number|undefined, context:string|undefined, contextId:number|undefined, subContextId:number|undefined, restrictedToUserId:string|undefined, privateOnly:boolean|undefined){
        //only load if user logged in
        if(this.authService.currentUserValue) return this.pdApiService.getVaultDocuments(this.authService.currentUserValue.id, clientId, context, contextId, subContextId, restrictedToUserId, privateOnly).pipe(map(r=> {return this.processGetVaultDocumentsResponse(r)}));
        else return of([] as VaultDocument[]);
    }

    private processGetVaultDocumentsResponse(r:VaultDocumentsResponse){
        //TODO implement cache here ?
        let vdArray:VaultDocument[]=[];
        if(r.success){
            r.vaultDocuments.forEach(dto=>vdArray.push(VaultDocument.mapFromDTO(dto)));
        }
        return vdArray;
    }

    public getVaultDocumentsWithFile(clientId:number|undefined, context:string|undefined, contextId:number|undefined, subContextId:number|undefined, restrictedToUserId:string|undefined, privateOnly:boolean|undefined){
        //only load if user logged in
        if(this.authService.currentUserValue) return this.pdApiService.getVaultDocumentsWithFile(this.authService.currentUserValue.id, clientId, context, contextId, subContextId, restrictedToUserId, privateOnly).pipe(map(r=> {return this.processGetVaultDocumentsWithFileResponse(r)}));
        else return of([]);
    }

    private processGetVaultDocumentsWithFileResponse(r:VaultDocumentsWithFileResponse){
        let vdArray:VaultDocument[]=[];
        if(r.success){
            r.vaultDocuments.forEach(dto=>{
                let vd = VaultDocument.mapFromDTO(dto);
                vd.file = new File([base64ToFile(dto.base64File, undefined)],vd.filepath);
                vdArray.push(vd);
            });
        }
        return vdArray;
    }

    public storeVaultDocument(vd:VaultDocument){
        if(!vd.id) {
            vd.businessId=this.authService.currentUserValue!.businessId;
            vd.addedByUser=this.authService.currentUserValue!.id;
        }
        vd.lastEditedByUser=this.authService.currentUserValue!.id;
        return this.pdApiService.storeVaultDocument(vd, this.authService.currentUserValue!.id).pipe(map(r=>this.processStoreVaultDocumentResponse(r)));
    }

    private processStoreVaultDocumentResponse(r:StoreVaultDocumentResponse){
        if(r.success){
            //let dto=r.vaultDocument;
            //let j=VaultDocument.mapFromDTO(dto);
            //not sure if we really need to process the dto here
            //once cache exist => update in cache
        }
        return r;
    }

    public deleteVaultDocument(vd:VaultDocument){
        if(!vd.id) {
            vd.businessId=this.authService.currentUserValue!.businessId;
            vd.addedByUser=this.authService.currentUserValue!.id;
        }
        vd.lastEditedByUser=this.authService.currentUserValue!.id;
        return this.pdApiService.deleteVaultDocument(vd, this.authService.currentUserValue!.id).pipe(map(r=>this.processDeleteVaultDocumentResponse(r)));
    }

    public updateVaultDocumentFolderPath(idList:Array<number>,folderStructureId:number,folderStructurePath:string){
        return this.pdApiService.updateVaultDocumentFolderPath(idList,folderStructureId,folderStructurePath).pipe(map((r:BasicResponse) => {return r;}));
    } 

    private processDeleteVaultDocumentResponse(r:BasicResponse){
        if(r.success){
            //once cache exist => remove from cache
        }
        return r;
    }

    /** propertyId: the property the document belongs to (required for client users, the server checks the document is visible to them) */
    public getVaultFile(id:number, propertyId?:number){
        return this.pdApiService.getVaultFile(id, this.authService.currentUserValue!.id, propertyId)
    }
    //#endregion

    //#region nda
    public draftNDAForAuction(request:DraftLegalDocumentRequest){
        return this.pdApiService.draftNDAForAuction(request).pipe(map(r=>this.draftNDAForAuctionResult(r,request)));
    }

    private draftNDAForAuctionResult(r:FileResponse, ar:DraftLegalDocumentRequest){
        if(r.success){
          //nothing to do right now
        }
        return r
    }
    
    public executeNDAForAuction(request:ExecuteLegalDocumentRequest){
        return this.pdApiService.executeNDAForAuction(request).pipe(map(r=>this.executeNDAForAuctionResult(r,request)));
    }
      
    private executeNDAForAuctionResult(r:Result, ar:ExecuteLegalDocumentRequest){
        if(r.success){
          //nothing for now
        }
        return r
    }
    //#endregion

    //#region buyer agreement
    public draftBuyerAgreementForAuction(request:DraftLegalDocumentRequest){
        return this.pdApiService.draftBuyerAgreementForAuction(request).pipe(map(r=>this.draftBuyerAgreementForAuctionResult(r,request)));
    }

    private draftBuyerAgreementForAuctionResult(r:FileResponse, ar:DraftLegalDocumentRequest){
        if(r.success){
          //nothing to do right now
        }
        return r
    }
    
    public executeBuyerAgreementForAuction(request:ExecuteLegalDocumentRequest){
        return this.pdApiService.executeBuyerAgreementForAuction(request).pipe(map(r=>this.executeBuyerAgreementForAuctionResult(r,request)));
    }
      
    private executeBuyerAgreementForAuctionResult(r:VaultDocumentsResponse, ar:ExecuteLegalDocumentRequest){
        if(r.success){
          //nothing for now
        }
        return r
    }
    //#endregion

    //#region auction authorization status
    public userAuctionAuthorizationStatus(request:UserAuctionAuthorizationStatusRequest):Observable<UserAuctionAuthorizationStatusResponse>{
        return this.pdApiService.userAuctionAuthorizationStatus(request);
    }
    //#endregion

    //#region property notes
     public getPropertyNotes(propertyId:number, forceReload:boolean){
        if(forceReload) this.propertyNotesCache[propertyId]=undefined;
        if(!this.authorized) return of(this.propertyNotesCache[propertyId]);
        // Data available
        if (this.propertyNotesCache[propertyId]) return of(this.propertyNotesCache[propertyId]);
        // Request pending
        else if (this.propertyNotesObservableCache[propertyId]) return this.propertyNotesObservableCache[propertyId];
        // New request needed
        else this.propertyNotesObservableCache[propertyId] = this.fetchPropertyNotes(propertyId);

        return this.propertyNotesObservableCache[propertyId];
    }

    public fetchPropertyNotes(propertyId:number){
         return this.pdApiService.getPropertyNotes(propertyId).pipe(map (r => this.processPropertyNotesResponse(r, propertyId)));
    }

    private processPropertyNotesResponse(r:PropertyNotesResponse, propertyId:number):PropertyNote[]{
        this.propertyNotesObservableCache[propertyId]=undefined;
        var notes = new Array<PropertyNote>();
        if(r.success && r.propertyNotes) {
            notes = r.propertyNotes.map(dto=>this.processPropertyNoteDTO(dto));
        }
        this.propertyNotesCache[propertyId]=notes;
        return notes;
    }

    private processPropertyNoteDTO(dto:PropertyNoteDTO):PropertyNote{
        return PropertyNote.mapFromDTO(dto);
    }

    public storePropertyNote(pn:PropertyNote){
        return this.pdApiService.storePropertyNote(pn).pipe(map ((r:StorePropertyNoteResponse) => {
            if(r.success){
                let pn = this.processPropertyNoteDTO(r.propertyNote!);
                if(this.propertyNotesCache[pn.propertyId]) {
                    let index = this.propertyNotesCache[pn.propertyId]!.findIndex(n => n.id===pn.id);
                    if(index>-1) this.propertyNotesCache[pn.propertyId]![index]=pn;
                    else this.propertyNotesCache[pn.propertyId]!.push(pn);
                }
            }
            return r;
        }));
    }

    public deletePropertyNote(pn:PropertyNote){
        return this.pdApiService.deletePropertyNote(pn).pipe(map ((r:BasicResponse) => {
            if(r.success){
                if(this.propertyNotesCache[pn.propertyId]) {
                    let index = this.propertyNotesCache[pn.propertyId]!.findIndex(n => n.id===pn.id);
                    if(index>-1) this.propertyNotesCache[pn.propertyId]!.splice(index,1)
                }
            }
            return r;
        }));
    }
    //#endregion

     //#region property expenses
    public getPropertyExpenses(propertyId:number){
        return this.pdApiService.getPropertyExpenses(propertyId).pipe(map (r => this.processPropertyExpensesResponse(r)));
    }
    private processPropertyExpensesResponse(r:PropertyExpensesResponse){
        return r.propertyExpenses.map(dto=>PropertyExpense.mapFromDTO(dto))
    }

    public storePropertyExpense(pe:PropertyExpense){
        return this.pdApiService.storePropertyExpense(pe);
    }
    public deletePropertyExpense(pe:PropertyExpense){
        return this.pdApiService.deletePropertyExpense(pe);
    }
    //#endregion

    //#region property parties
    public getPropertyParties(propertyId:number){
        return this.pdApiService.getPropertyParties(propertyId).pipe(map (r => this.processPropertyParties(r)));
    }
    private processPropertyParties(dtoArray:PropertyPartyDTO[]){
        return dtoArray.map(dto=>PropertyParty.mapFromDTO(dto));
    }
    //#endregion

    //#region property tasks
    public getPropertyTasks(propertyId:number){
        return this.pdApiService.getPropertyTasks(propertyId).pipe(map (r => this.processPropertyTasks(r)));
    }
    private processPropertyTasks(dtoArray:PropertyTaskDTO[]){
        return dtoArray.map(dto=>PropertyTask.mapFromDTO(dto));
    }
    //#endregion

    //#region property preservation
    public getPropertyPreservation(propertyId:number){
        return this.pdApiService.getPropertyPreservation(propertyId).pipe(map (r => this.processPropertyPreservation(r)));
    }
    private processPropertyPreservation(dtoArray:PropertyPreservationDTO[]){
        return dtoArray.map(dto=>PropertyPreservation.mapFromDTO(dto));
    }
    //#endregion

    //#region property management
    public getPropertyManagement(propertyId:number){
        return this.pdApiService.getPropertyManagement(propertyId).pipe(map (r => this.processPropertyManagement(r)));
    }
    private processPropertyManagement(dtoArray:PropertyManagementDTO[]){
        return dtoArray.map(dto=>PropertyManagement.mapFromDTO(dto));
    }
    //#endregion

    //#region property master settings
    private propertyMasterCatalogCache?:Observable<PropertyMasterCatalogItemDTO[]>;

    /** components of the property master and their default sale types, loaded once */
    public getPropertyMasterCatalog():Observable<PropertyMasterCatalogItemDTO[]>{
        if(!this.propertyMasterCatalogCache){
            this.propertyMasterCatalogCache = this.pdApiService.getPropertyMasterCatalog().pipe(
                map(r => Array.isArray(r) ? r as PropertyMasterCatalogItemDTO[] : []),
                tap(items => { if(items.length==0) this.propertyMasterCatalogCache=undefined; }), //failed: load again next time
                shareReplay(1));
        }
        return this.propertyMasterCatalogCache;
    }

    /** which property master items ('section.key') the current user sees; undefined if it couldn't be loaded */
    public getPropertyVisibility(propertyId:number):Observable<Record<string,boolean>|undefined>{
        return this.pdApiService.getPropertyVisibility(propertyId).pipe(map((r:any) => (r && typeof r === 'object' && r.success !== false) ? r as Record<string,boolean> : undefined));
    }

    public getPropertyMasterSettings(propertyId:number){
        return this.pdApiService.getPropertyMasterSettings(propertyId).pipe(map (r => this.processPropertyMasterSettings(r)));
    }
    private processPropertyMasterSettings(dto:PropertyMasterSettingsDTO){
        return dto? PropertyMasterSettings.mapFromDTO(dto):undefined;
    }
    public storePropertyMasterSettings(pms:PropertyMasterSettings){
        return this.pdApiService.storePropertyMasterSettings(pms);
    }
    //#endregion

    //#region blob loading
    public downloadData(url:string){
        return this.pdApiService.downloadData(url)
    }

    public getPropertyImageFile(propertyId:number, imageId:number){
        return this.pdApiService.getPropertyImageFile(propertyId, imageId);
    }
    //#endregion

    //#region trash (removed images, deleted vault documents)
    public getPropertyTrash(propertyId:number):Observable<PropertyTrashResponse>{
        return this.pdApiService.getPropertyTrash(propertyId);
    }

    public restoreFromTrash(propertyId:number, kind:TrashItemKind, id:number):Observable<RestoreFromTrashResponse>{
        return this.pdApiService.restoreFromTrash(propertyId, kind, id);
    }

    public deleteFromTrash(propertyId:number, kind:TrashItemKind, id:number):Observable<Result>{
        return this.pdApiService.deleteFromTrash(propertyId, kind, id);
    }
    //#endregion

    //#region property photos (albums), saved immediately
    public getPropertyImageAlbums(propertyId:number):Observable<PropertyImageAlbumsResponse>{
        return this.pdApiService.getPropertyImageAlbums(propertyId);
    }

    //BasicResponse: longCode = album id
    public storePropertyImageAlbum(propertyId:number, id:number|undefined, name:string, visibleTo:number|undefined):Observable<BasicResponse>{
        return this.pdApiService.storePropertyImageAlbum(propertyId, id, name, visibleTo);
    }

    public storePropertyImageAlbumOrder(propertyId:number, albumIds:number[]):Observable<BasicResponse>{
        return this.pdApiService.storePropertyImageAlbumOrder(propertyId, albumIds);
    }

    public deletePropertyImageAlbum(propertyId:number, id:number):Observable<BasicResponse>{
        return this.pdApiService.deletePropertyImageAlbum(propertyId, id);
    }

    public getPropertyAlbumImages(propertyId:number, albumId:number|undefined, offset:number, top:number):Observable<PropertyAlbumImagesResponse>{
        return this.pdApiService.getPropertyAlbumImages(propertyId, albumId, offset, top);
    }

    public getPropertyImageThumbnail(propertyId:number, imageId:number){
        return this.pdApiService.getPropertyImageThumbnail(propertyId, imageId);
    }

    public uploadPropertyImage(propertyId:number, albumId:number, file:File, source?:number, sourceImageId?:number, proof?:ProofLink):Observable<HttpEvent<any>>{
        return this.pdApiService.uploadPropertyImage(propertyId, albumId, file, source, sourceImageId, proof);
    }

    public getPropertyProofSummary(propertyId:number):Observable<PropertyProofSummaryResponse>{
        return this.pdApiService.getPropertyProofSummary(propertyId);
    }

    public getPropertyProofFollowUps(propertyId:number, categoryType:string):Observable<PropertyProofFollowUpsResponse>{
        return this.pdApiService.getPropertyProofFollowUps(propertyId, categoryType);
    }

    public storePropertyProofFollowUp(request:{ propertyId:number, categoryType:string, categorySubType:string, categoryId?:number|null, workDate:string, vendorId?:number|null, reason:string }):Observable<BasicResponse>{
        return this.pdApiService.storePropertyProofFollowUp(request);
    }

    public resolvePropertyProofFollowUp(request:{ propertyId:number, id:number, resolution?:string, resolvedWorkDate?:string, reopen?:boolean, delete?:boolean }):Observable<BasicResponse>{
        return this.pdApiService.resolvePropertyProofFollowUp(request);
    }

    public getPropertyPhotoUploadLinks(propertyId:number):Observable<PhotoUploadLinksResponse>{
        return this.pdApiService.getPropertyPhotoUploadLinks(propertyId);
    }

    public storePropertyPhotoUploadLink(request:StorePhotoUploadLinkRequest):Observable<PhotoUploadLinkResponse>{
        return this.pdApiService.storePropertyPhotoUploadLink(request);
    }

    public updatePropertyPhotoUploadLink(request:{ propertyId:number, id:number, extendDays?:number, revoke?:boolean, newLink?:boolean, sendEmail?:boolean }):Observable<PhotoUploadLinkResponse>{
        return this.pdApiService.updatePropertyPhotoUploadLink(request);
    }

    public getPropertyPhotoUploads(propertyId:number):Observable<VendorPhotoUploadsResponse>{
        return this.pdApiService.getPropertyPhotoUploads(propertyId);
    }

    public reviewPropertyPhotoUploads(request:ReviewVendorPhotoUploadsRequest):Observable<BasicResponse>{
        return this.pdApiService.reviewPropertyPhotoUploads(request);
    }

    public getPropertyImageTags(propertyId:number):Observable<PropertyImageTagsResponse>{
        return this.pdApiService.getPropertyImageTags(propertyId);
    }

    public getPropertyImageTagSuggestions():Observable<PropertyImageTagSuggestionsResponse>{
        return this.pdApiService.getPropertyImageTagSuggestions();
    }

    public storePropertyImageTags(propertyId:number, imageIds:number[], add:string[], remove:string[]):Observable<BasicResponse>{
        return this.pdApiService.storePropertyImageTags(propertyId, imageIds, add, remove);
    }

    public createPropertyPhotoReport(request:PropertyPhotoReportRequest):Observable<PropertyPhotoReportResponse>{
        return this.pdApiService.createPropertyPhotoReport(request);
    }

    public getPropertyProofVisits(propertyId:number, categoryType:string, from?:string):Observable<PropertyProofVisitsResponse>{
        return this.pdApiService.getPropertyProofVisits(propertyId, categoryType, from);
    }

    public getPropertyProofImages(propertyId:number, categoryType:string, categorySubType:string, categoryId?:number):Observable<PropertyAlbumImagesResponse>{
        return this.pdApiService.getPropertyProofImages(propertyId, categoryType, categorySubType, categoryId);
    }

    public linkPropertyImages(propertyId:number, imageIds:number[], link:ProofLink):Observable<BasicResponse>{
        return this.pdApiService.linkPropertyImages(propertyId, imageIds, link);
    }

    public unlinkPropertyImages(propertyId:number, imageIds:number[]):Observable<BasicResponse>{
        return this.pdApiService.unlinkPropertyImages(propertyId, imageIds);
    }

    public updatePropertyImageProof(propertyId:number, imageIds:number[], change:ProofChange):Observable<BasicResponse>{
        return this.pdApiService.updatePropertyImageProof(propertyId, imageIds, change);
    }

    public movePropertyImages(propertyId:number, imageIds:number[], targetAlbumId:number):Observable<BasicResponse>{
        return this.pdApiService.movePropertyImages(propertyId, imageIds, targetAlbumId);
    }

    public copyPropertyImages(propertyId:number, imageIds:number[], targetAlbumId:number):Observable<CopyPropertyImagesResponse>{
        return this.pdApiService.copyPropertyImages(propertyId, imageIds, targetAlbumId);
    }

    public trashPropertyImages(propertyId:number, imageIds:number[]):Observable<BasicResponse>{
        return this.pdApiService.trashPropertyImages(propertyId, imageIds);
    }

    public storePropertyImageDescription(propertyId:number, id:number, description:string):Observable<BasicResponse>{
        return this.pdApiService.storePropertyImageDescription(propertyId, id, description);
    }

    public storePropertyImagePool(propertyId:number, pool:PropertyImagePoolDTO[]):Observable<BasicResponse>{
        return this.pdApiService.storePropertyImagePool(propertyId, pool);
    }
    //#endregion

    //#region csv file samling
    public sampleFile(file:File, fileType:string, fieldDelimiter:string, hasHeader:boolean, startAtRow:number){
        return this.pdApiService.sampleFile(file,fileType, fieldDelimiter, hasHeader, startAtRow);
    }
    //#endregion


}